<script setup lang="ts">
  import type { TrainingMatrixDocumentEntry } from '@/entities/Training';
  import { Checkbox, DefaultTable, IconImage, SimpleLoading, TableHeader, Tooltip } from '@/shared/ui';
  import { Can } from '@/entities/Permission';
  import Eye from '@/shared/assets/icons/EyeGray.svg';

  const props = withDefaults(
    defineProps<{
      documents: TrainingMatrixDocumentEntry[];
      loading?: boolean;
      selectable?: boolean;
      isSelected?: (documentId: number) => boolean;
      allSelected?: boolean;
      partiallySelected?: boolean;
      selectedCount?: number;
    }>(),
    {
      selectable: false,
      isSelected: () => false,
      allSelected: false,
      partiallySelected: false,
      selectedCount: 0,
    }
  );

  const emit = defineEmits<{
    view: [documentRevisionId: number];
    toggle: [documentId: number];
    'toggle-all': [];
  }>();

  const prefixThClass = (selectable: boolean): string =>
    selectable
      ? 'pl-2 pr-4 py-4.5 text-left text-xs font-semibold text-gray-07 whitespace-nowrap'
      : 'pl-6 pr-4 py-4.5 text-left text-xs font-semibold text-gray-07 whitespace-nowrap';

  const prefixTdClass = (selectable: boolean): string =>
    selectable
      ? 'pl-2 pr-4 py-4.5 text-sm whitespace-nowrap text-gray-07'
      : 'pl-6 pr-4 py-4.5 text-sm whitespace-nowrap text-gray-07';
</script>

<template>
  <DefaultTable data-testid="create-training-documents-table">
    <colgroup>
      <col
        v-if="selectable"
 class="w-12"
      >
      <col class="w-40">
      <col>
      <col class="w-24">
    </colgroup>
    <thead>
      <tr>
        <th
          v-if="selectable"
          class="py-4.5 pl-6 pr-2 text-center align-middle"
        >
          <Checkbox
            id="create-training-documents-select-all"
            data-testid="create-training-documents-select-all"
            :checked="allSelected"
            :indeterminate="partiallySelected"
            @toggle="emit('toggle-all')"
          />
        </th>
        <TableHeader
          text="Prefixo"
          :th-class="prefixThClass(selectable)"
        />
        <TableHeader
          text="Título"
          th-class="px-4 py-4.5 text-left text-xs font-semibold text-gray-07"
        />
        <th class="px-6 py-4.5" />
      </tr>
    </thead>
    <tbody class="divide-y divide-gray-02 border-t border-gray-02 bg-white-01">
      <tr
        v-if="loading"
        data-testid="create-training-documents-loading"
      >
        <td
          colspan="100"
          class="px-6 py-6"
        >
          <div class="flex justify-center">
            <SimpleLoading />
          </div>
        </td>
      </tr>
      <tr
        v-for="document in loading ? [] : documents"
        :key="document.id"
        data-testid="create-training-document-row"
      >
        <td
          v-if="selectable"
          class="py-4.5 pl-6 pr-2 text-center align-middle"
        >
          <Checkbox
            :id="document.id"
            data-testid="create-training-document-checkbox"
            :checked="isSelected(document.id)"
            @toggle="emit('toggle', document.id)"
          />
        </td>
        <td :class="prefixTdClass(selectable)">
          {{ document.code }}
        </td>
        <td class="px-4 py-4.5 text-sm text-gray-07">
          {{ document.name }}
        </td>
        <td class="px-6 py-4.5">
          <div class="flex items-center justify-end">
            <Can :permissions="[{ action: 'manage', subject: 'DocumentManagement' }]">
              <Tooltip content="Visualizar">
                <button
                  type="button"
                  aria-label="Visualizar documento"
                  class="inline-flex items-center justify-center"
                  @click="emit('view', document.id)"
                >
                  <IconImage
                    :src="Eye"
                    alt="Ícone de Visualização"
                  />
                </button>
              </Tooltip>
            </Can>
          </div>
        </td>
      </tr>
      <tr v-if="!loading && documents.length === 0">
        <td
          colspan="100"
          data-testid="create-training-documents-empty"
          class="px-6 py-6 text-center text-sm text-gray-05"
        >
          Nenhum documento encontrado para o cargo e revisão selecionados
        </td>
      </tr>
    </tbody>
  </DefaultTable>
  <p
    v-if="!loading"
    data-testid="create-training-documents-total"
    class="py-3 text-sm text-gray-07"
  >
    {{
      selectable
        ? `Total de documentos selecionados: ${selectedCount}`
        : `Total de documentos: ${documents.length}`
    }}
  </p>
</template>
